import { ChevronLeft, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { authService } from "../../services/auth.service";

export function ResetPasswordStep({ email, onBack, onDone }) {
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");

    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword({ email, code, newPassword: password });
      onDone();
    } catch (err) {
      setError(err?.message || "This reset code is invalid or has expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col md:p-8 px-2 py-3 h-full">
      <button
        onClick={onBack}
        className="mb-8 w-10 h-10 flex items-center justify-start rounded-full hover:bg-gray-50"
      >
        <ChevronLeft size={28} color="#0A4730" />
      </button>

      <h1 className="md:text-2xl text-xl font-black uppercase mb-4 text-center">
        Reset Password
      </h1>

      <p className="text-gray-500 text-[15px] mb-8 text-center">
        Enter the 6-digit code sent to{" "}
        <span className="font-semibold text-[#0A2540]">{email}</span> and choose a
        new password.
      </p>

      <form onSubmit={handleReset} className="space-y-5">
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit code"
          className="w-full bg-[#F3F4F6] rounded-xl p-4 outline-none tracking-[0.4em] text-center font-semibold"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          required
        />

        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="New password"
            className="w-full bg-[#F3F4F6] rounded-xl p-4 outline-none"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        <input
          type={showPassword ? "text" : "password"}
          placeholder="Confirm new password"
          className="w-full bg-[#F3F4F6] rounded-xl p-4 outline-none"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />

        {error && <p className="text-sm text-red-500">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary font-bold py-4 rounded-xl w-full flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {loading && <Loader2 size={18} className="animate-spin" />}
          {loading ? "Resetting…" : "Reset password"}
        </button>
      </form>
    </div>
  );
}
